import React, { useEffect, useState } from "react";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import AndroidIcon from "@mui/icons-material/Android";

// Building blocks shared by the walkthrough pages (Quick Notes, Share Info, Long Shot).

export const accentText = "text-[#eb4d5c] dark:text-[#c9a7ff]";
export const mutedText = "text-[#555] dark:text-[#c9cfe3]";

export const filledButton =
  "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-lg font-semibold bg-[#eb4d5c] text-white hover:bg-[#d63f4e] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#b48ff5] transition-colors";
export const outlineButton =
  "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-lg font-semibold border-2 border-[#eb4d5c] text-[#eb4d5c] hover:bg-[#eb4d5c] hover:text-white dark:border-[#c9a7ff] dark:text-[#c9a7ff] dark:hover:bg-[#c9a7ff] dark:hover:text-[#0f193d] transition-colors";

// Phone screenshots are 600x1233 (the emulator's 1080x2220 screen, scaled down).
// The Android apps are downloaded from GitHub releases, where each version is built and signed
// by the repository's release workflow. "latest/download" always serves the newest release, so
// these links don't change when a new version ships.
export function ApkDownload({ repo, file }) {
  return (
    <a href={`https://github.com/${repo}/releases/latest/download/${file}`} className={outlineButton}>
      <AndroidIcon fontSize="small" aria-hidden="true" />
      Download APK
    </a>
  );
}

// The newest release's version, size and date, read from GitHub's public API so the page never
// shows a stale version. Cached for the browser session to stay well under GitHub's limit of
// 60 unauthenticated requests an hour. If the request fails, the note simply leaves them out.
function useLatestRelease(repo, file) {
  const [release, setRelease] = useState(null);

  useEffect(() => {
    const key = `release:${repo}`;
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        setRelease(JSON.parse(cached));
        return undefined;
      }
    } catch (e) {
      // Storage can be unavailable (private windows, blocked site data); just fetch.
    }

    let cancelled = false;
    fetch(`https://api.github.com/repos/${repo}/releases/latest`, { headers: { Accept: "application/vnd.github+json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        const asset = (data.assets || []).find((a) => a.name === file);
        const info = {
          version: String(data.tag_name || "").replace(/^v/, ""),
          size: asset ? asset.size : null,
          date: data.published_at,
        };
        try {
          sessionStorage.setItem(key, JSON.stringify(info));
        } catch (e) {
          // Not cached this time; harmless.
        }
        if (!cancelled) setRelease(info);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [repo, file]);

  return release;
}

function describeRelease({ version, size, date }) {
  const parts = [];
  if (version) parts.push(`Version ${version}`);
  if (size) parts.push(`${(size / 1e6).toFixed(1)} MB`);
  if (date) {
    const when = new Date(date);
    if (!Number.isNaN(when.getTime())) {
      parts.push(`released ${when.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`);
    }
  }
  return parts.join(" · ");
}

export function ApkNote({ repo, file, minAndroid, className = "" }) {
  const release = useLatestRelease(repo, file);
  const summary = release ? describeRelease(release) : "";

  return (
    <p className={`text-sm ${mutedText} ${className}`}>
      {summary && (
        <>
          <span className="font-semibold">{summary}</span>
          <br />
        </>
      )}
      For Android {minAndroid} or newer. Signed builds come from{" "}
      <a href={`https://github.com/${repo}/releases`} target="_blank" rel="noreferrer" className="underline underline-offset-2">
        GitHub Releases
      </a>
      , not Google Play, so Android asks before installing.
    </p>
  );
}

export const PHONE = { width: 600, height: 1233 };

// Shows a screenshot in the app theme chosen in the sticky bar. With no choice made, both
// versions are rendered and the site's own dark mode decides which one is visible.
export function Themed({ shot, appTheme, alt, width, height, className }) {
  const common = { width, height, loading: "lazy", decoding: "async" };
  if (appTheme) {
    return <img src={shot[appTheme]} alt={alt} {...common} className={`block ${className}`} />;
  }
  return (
    <>
      <img src={shot.light} alt={alt} {...common} className={`block dark:hidden ${className}`} />
      <img src={shot.dark} alt={alt} {...common} className={`hidden dark:block ${className}`} />
    </>
  );
}

export function Phone({ screen, appTheme, size = "w-[42vw] max-w-[230px] lg:w-[240px] lg:max-w-none" }) {
  return (
    <figure className={`shrink-0 ${size}`}>
      <div className="rounded-[1.6rem] lg:rounded-[2rem] bg-[#16171b] p-[5px] lg:p-[7px] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.45)] ring-1 ring-black/10 dark:ring-white/15">
        <Themed
          shot={screen.shot}
          appTheme={screen.theme || appTheme}
          alt={screen.alt}
          width={PHONE.width}
          height={PHONE.height}
          className="w-full h-auto rounded-[1.3rem] lg:rounded-[1.6rem]"
        />
      </div>
      {screen.caption && (
        <figcaption className={`pt-3 text-center text-sm font-semibold ${mutedText}`}>{screen.caption}</figcaption>
      )}
    </figure>
  );
}

// A desktop screenshot in a simple browser window, for web apps.
export function Browser({ screen, appTheme, url = "mohamedoulal.com/healthsearch", size = "w-full lg:w-[560px]" }) {
  return (
    <figure className={`shrink-0 max-w-full ${size}`}>
      <div className="overflow-hidden rounded-xl lg:rounded-2xl bg-white dark:bg-[#1d2430] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.4)] ring-1 ring-black/10 dark:ring-white/15">
        <div className="flex items-center gap-1.5 border-b border-black/5 bg-[#eceef2] px-3 py-2 dark:border-white/10 dark:bg-[#2a3240]" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-3 min-w-0 flex-1 truncate rounded-md bg-white px-3 py-0.5 text-center text-[12px] text-[#555] dark:bg-[#1d2430] dark:text-[#c9cfe3]">
            {url}
          </span>
        </div>
        <Themed
          shot={screen.shot}
          appTheme={screen.theme || appTheme}
          alt={screen.alt}
          width={screen.width}
          height={screen.height}
          className="w-full h-auto"
        />
      </div>
      {screen.caption && (
        <figcaption className={`pt-3 text-center text-sm font-semibold ${mutedText}`}>{screen.caption}</figcaption>
      )}
    </figure>
  );
}

// "Light" and "Dark" pick a theme for every screenshot. Until one is picked, the button matching
// the site's current theme is shown as selected, using the same dark: classes as the site.
export function ThemeSwitch({ appTheme, setAppTheme }) {
  const base = "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors";
  const on = "bg-[#eb4d5c] text-white dark:bg-[#c9a7ff] dark:text-[#0f193d]";
  const off = "text-[#333] dark:text-[#e6e8ef] hover:bg-black/5 dark:hover:bg-white/10";
  const light = appTheme === "light" ? on : appTheme === "dark" ? off
    : "bg-[#eb4d5c] text-white dark:bg-transparent dark:text-[#e6e8ef] dark:hover:bg-white/10";
  const dark = appTheme === "dark" ? on : appTheme === "light" ? off
    : "text-[#333] hover:bg-black/5 dark:bg-[#c9a7ff] dark:text-[#0f193d]";
  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className={`hidden sm:inline text-sm font-medium ${mutedText}`}>App theme</span>
      <div role="group" aria-label="App theme in screenshots" className="flex rounded-full border border-black/10 dark:border-white/15 p-0.5 bg-white/70 dark:bg-white/5">
        <button type="button" aria-pressed={appTheme === "light"} onClick={() => setAppTheme("light")} className={`${base} ${light}`}>
          <LightModeOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
          Light
        </button>
        <button type="button" aria-pressed={appTheme === "dark"} onClick={() => setAppTheme("dark")} className={`${base} ${dark}`}>
          <DarkModeOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
          Dark
        </button>
      </div>
    </div>
  );
}

// The current step is the last one whose top has scrolled above a line a third of the way down
// the window, so the sticky bar highlights the step being read.
export function useActiveStep(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight / 3;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [ids]);
  return active;
}

// Sticky bar: where you are in the tour, and which app theme the screenshots show.
// Pages without light and dark screenshots leave out setAppTheme and get no theme switch.
export function TourBar({ steps, active, appTheme, setAppTheme }) {
  return (
    <div className="sticky top-0 z-20 bg-[#f0f0f0]/90 dark:bg-[#3d4851]/90 backdrop-blur border-y border-black/5 dark:border-white/10">
      <div className="w-full max-w-5xl mx-auto px-4 py-2 flex items-center gap-3">
        <nav aria-label="Tour steps" className="flex-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ol className="flex gap-1 w-max">
            {steps.map((step, i) => (
              <li key={step.id}>
                <a
                  href={`#${step.id}`}
                  aria-current={active === step.id ? "step" : undefined}
                  className={`block whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                    active === step.id
                      ? "bg-[#eb4d5c]/15 text-[#c43646] dark:bg-[#c9a7ff]/20 dark:text-[#e2d2ff]"
                      : "text-[#444] dark:text-[#d6d9e3] hover:bg-black/5 dark:hover:bg-white/10"
                  }`}
                >
                  <span className="opacity-60 pr-1">{i + 1}</span>
                  {step.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>
        {setAppTheme && <ThemeSwitch appTheme={appTheme} setAppTheme={setAppTheme} />}
      </div>
    </div>
  );
}

// Feature tiles under the hero; each one jumps to its part of the tour.
export function FeatureTiles({ features }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {features.map(({ icon: Icon, title, text, href }) => (
        <li key={title}>
          <a href={href} className="h-full flex gap-3 rounded-2xl p-4 bg-white dark:bg-white/5 shadow-sm ring-1 ring-black/5 dark:ring-white/10 hover:ring-[#eb4d5c] dark:hover:ring-[#c9a7ff] transition-shadow">
            <span className="shrink-0 w-10 h-10 rounded-full grid place-items-center bg-[#fde3e5] text-[#c43646] dark:bg-[#c9a7ff]/20 dark:text-[#c9a7ff]">
              <Icon fontSize="small" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-lg font-semibold leading-snug">{title}</span>
              <span className={`block text-[15px] leading-snug ${mutedText}`}>{text}</span>
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

// One numbered step of the tour: text on one side, screenshots on the other, alternating.
export function TourStep({ step, index, total, appTheme, children }) {
  return (
    <li
      id={step.id}
      className="scroll-mt-20 py-12 lg:py-16 border-b border-black/5 dark:border-white/10 last:border-b-0 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-14"
    >
      <div className={`lg:flex-1 text-center lg:text-left ${index % 2 === 1 ? "lg:order-2" : ""}`}>
        <p className={`text-sm font-bold tracking-widest uppercase ${accentText}`}>
          Step {index + 1} of {total}
        </p>
        <h3 className="text-3xl lg:text-4xl font-bold pt-1 pb-3">{step.title}</h3>
        <p className="text-lg lg:text-xl leading-relaxed text-[#333] dark:text-[#d6d9e3]">{step.body}</p>
        {step.detail && <p className={`pt-3 text-base leading-relaxed ${mutedText}`}>{step.detail}</p>}
      </div>
      <div className={`flex min-w-0 justify-center gap-4 lg:gap-6 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
        {step.screens &&
          step.screens.map((screen) =>
            screen.browser ? (
              <Browser key={screen.caption} screen={screen} appTheme={appTheme} />
            ) : (
              <Phone key={screen.caption} screen={screen} appTheme={appTheme} />
            )
          )}
        {children}
      </div>
    </li>
  );
}

// Engineering notes cards for the "How it's built" section.
export function InternalsGrid({ items }) {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <li key={item.title} className="rounded-2xl p-5 bg-white dark:bg-white/5 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
          <h3 className="text-xl font-semibold pb-2">{item.title}</h3>
          <p className="text-base leading-relaxed text-[#333] dark:text-[#d6d9e3]">{item.text}</p>
        </li>
      ))}
    </ul>
  );
}
